import React, { useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import { useTheme } from "@mui/styles";
import DashboardContainer from "../../components/dashboard-container";
import { supplierService } from "../../utils/services/supplierService";
import SupplierList from "../../components/supplier-list";
import CustomPagination from "../../components/pagination";
import SupplierModal from "../../components/supplierModal";
import buildQuery from "../../utils/query-builder";
import { supplierFilterInitialState } from "../../utils/filter-config";
import CustomTypography from "../../globalStyles/fonts";
import ImportIcon from "../../globalStyles/icons/importIcon";
import { isMobile } from "../../globalStyles/mobile";

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) =>
          x?.key === action?.payload?.key ? { ...x, ...action.payload } : x,
        ),
      };
    case "SET_OPTIONS":
      return {
        ...state,
        columns: state.columns.map((col) =>
          col.key === action.payload.key
            ? { ...col, options: action.payload.options }
            : col,
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };
    default:
      return state;
  }
}

export default function Supplier() {
  const [filterData, dispatch] = useReducer(
    filterReducer,
    supplierFilterInitialState,
  );
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = React.useState(0);
  const [dataCount, setDataCount] = useState(filterData.limit);
  const [searchValue, setSearchValue] = useState("");
  const [clientFilter, setClientFilter] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [fetchData, setFetchData] = useState(0);
  const clientId = localStorage.getItem("client_id");
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;

  const updateFilterData = () => {
    localStorage.setItem(
      "supplier-filter-data",
      JSON.stringify({
        filters: filterData.columns.map((x) => ({
          key: x.key,
          values: x.filter,
        })),
        limit: filterData.limit,
      }),
    );
  };

  useEffect(() => {
    let data = localStorage.getItem("supplier-filter-data");
    if (data) {
      localStorage.removeItem("supplier-filter-data");
      dispatch({
        type: "EXISTING_DATA",
        data: JSON.parse(data),
      });
    }
  }, []);

  const fetchSupplierData = async (signal) => {
    setLoading(true);
    try {
      let response = await supplierService.fetch(
        searchValue,
        clientId || clientFilter?._id,
        buildQuery(filterData, page),
      );
      setSuppliers(response.data);
      setDataCount(response?.total);
    } catch (error) {
      if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
        console.error("Fetch failed:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchSupplierData(controller.signal);
    return () => {
      controller.abort();
    };
  }, [page, filterData, searchValue, clientFilter, fetchData]);

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  return (
    <>
      <DashboardContainer
        searchBarPlaceholder={"Search by Supplier Name"}
        onChangeFilter={handleFilterChange}
        filterData={filterData}
        fields={filterData.columns}
        hideFilterClient
        hideUploadButton
        showRefreshButton
        headerButtons={
          clientId ? (
            isMobile() ? (
              <IconButton
                onClick={() => {
                  updateFilterData();
                  navigate("/supplier-import");
                }}
              >
                <ImportIcon fill={primary_color} />
              </IconButton>
            ) : (
              <Button
                size="small"
                style={{ textTransform: "none" }}
                variant="contained"
                onClick={() => {
                  updateFilterData();
                  navigate("/supplier-import");
                }}
              >
                <ImportIcon fill="#FFF" />
                <CustomTypography fontSize="14px" fontWeight={500} color="#FFFFFF">
                  Import
                </CustomTypography>
              </Button>
            )
          ) : null
        }
        onSearchData={(x) => {
          if (x.trim()) {
            setSearchValue(x);
            setPage(0);
          } else {
            setSearchValue("");
          }
        }}
        onClickAddButton={() => setOpenModal(true)}
        header={"Supplier List"}
        onSelectClient={(e) => {
          setClientFilter(e);
        }}
        setViewMode={setViewMode}
        title={"+ Supplier"}
        onClickRefresh={() => setFetchData(fetchData + 1)}
      >
        <SupplierModal
          selectedSupplier={selectedSupplier}
          handleClose={() => {
            setOpenModal(false);
            setSelectedSupplier(null);
          }}
          open={openModal}
          onAddData={() => setFetchData(fetchData + 1)}
          clientId={clientId}
        />

        <SupplierList
          onDeleteData={() => setFetchData(fetchData + 1)}
          onEditData={(e) => {
            setSelectedSupplier(e);
            setOpenModal(true);
          }}
          data={suppliers}
          viewMode={viewMode}
          deleteTitle={"Delete Supplier"}
          selectedSupplier={selectedSupplier}
          setSelectedSupplier={setSelectedSupplier}
          onChangeFilter={handleFilterChange}
          filterData={filterData}
          currentPage={page}
          itemsPerPage={filterData.limit}
          clientId={clientId}
          loading={loading}
          setLoading={setLoading}
        />

        <CustomPagination
          dataCount={dataCount}
          rowsPerPage={filterData.limit}
          page={page}
          setPage={setPage}
          setRowsPerPage={(e) =>
            dispatch({
              type: "UPDATE_LIMIT",
              payload: e,
            })
          }
          disableNext={dataCount <= (page + 1) * filterData.limit}
        />
      </DashboardContainer>
    </>
  );
}
